/**
 * Salon d'attente d'un défi live privé (migrations 0650, 0658) : compte à
 * rebours (ou attente de l'organisateur), joueurs en grands avatars, ce qui
 * attend les joueurs ; pour l'HÔTE en plus : code + QR + lien à partager,
 * « Démarrer maintenant », « Programmer », « Changer la cover », « Annuler ».
 * Rendu par le player live (écran sombre, `LiveArena`) pendant l'attente.
 */

import { useRef, useState, type ReactNode } from 'react';
import { isFirstScratchUse } from '@/features/scratch-tutorial/first-use';
import {
  CalendarClock,
  Check,
  ImageIcon,
  ListChecks,
  LogOut,
  Play,
  Share2,
  Sparkles,
  Trophy,
  Users,
  Wifi,
  X,
} from 'lucide-react';
import { AvatarCircle, cn } from '@jeraisonne/ui';
import { InviteLinkField } from '@/components/invite-link-field';
import { QrCodeImage } from '@/components/qr-code';
import { shareContent } from '@/lib/native-share';
import { useSetPrivateLiveCover } from '@/lib/live-covers';
import {
  formatJoinCode,
  privateLiveErrorMessage,
  privateLiveSummary,
  privateLiveUrl,
  scheduleError,
  shouldOfferScratchPractice,
  usePrivateLive,
  useLeavePrivateLive,
  useStartPrivateLive,
  playersCapLabel,
  type PrivateLive,
} from '@/lib/private-live';
import { ArenaInfoList, ArenaPanel } from '@/features/live-ui/live-arena';
import { liveCtaClass } from '@/features/live-ui/live-cover-card';
import { LiveCoverPicker } from '@/features/live-ui/live-cover-picker';
import { ScratchPracticeCard } from './scratch-practice';

/** Joueurs affichés en grands avatars (au-delà : « +N »). */
export const LOBBY_MAX_AVATARS = 8;

/** Valeur d'un `<input type="datetime-local">` (heure locale). */
function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type LobbyPlayer = PrivateLive['players'][number];

/**
 * Ordre des joueurs du salon : l'organisateur, moi, les connectés, puis les
 * autres (ordre d'arrivée conservé dans chaque groupe). PUR.
 */
export function lobbyPlayersOrder(players: LobbyPlayer[]): LobbyPlayer[] {
  const weight = (p: LobbyPlayer) => (p.is_host ? 0 : p.is_me ? 1 : p.connected ? 2 : 3);
  return players
    .map((p, i) => ({ p, i }))
    .sort((a, b) => weight(a.p) - weight(b.p) || a.i - b.i)
    .map(({ p }) => p);
}

export function PrivateLobby({
  code,
  userId,
  awaitingHost,
  countdown,
  isGuest = false,
}: {
  code: string;
  /** joueur invité (session anonyme) : on lui propose toujours la feuille */
  isGuest?: boolean;
  userId: string | null;
  /** Le départ n'est pas encore décidé (sinon un compte à rebours s'affiche). */
  awaitingHost: boolean;
  /** Compte à rebours fourni par le player quand l'heure de départ est fixée. */
  countdown: ReactNode;
}) {
  const { data: live } = usePrivateLive(code, userId);
  // décidé une fois : la carte reste après l'entraînement (on peut le refaire)
  const [seenTutorial] = useState(() => !isFirstScratchUse());
  const offerPractice =
    !!live &&
    shouldOfferScratchPractice({
      settings: live.settings,
      stepCount: live.step_count,
      isGuest,
      seenTutorial,
    });

  return (
    <div className="space-y-4">
      <ArenaPanel className="flex flex-col items-center gap-3 py-7 text-center">
        {awaitingHost ? (
          <>
            <p className="font-display text-xl font-bold text-white">
              {live?.is_host
                ? 'Lance le défi quand tout le monde est là'
                : 'En attente de l’organisateur'}
            </p>
            {!live?.is_host && live?.host && (
              <p className="text-ink-600 text-sm">
                {live.host.display_name ?? 'L’organisateur'} va démarrer le défi. Reste sur cette
                page, ça démarre tout seul.
              </p>
            )}
          </>
        ) : (
          <>
            <p className="text-ink-700 text-sm font-semibold">Le défi commence dans</p>
            {countdown}
            <p className="text-ink-500 text-xs">Reste sur cette page, ça démarre tout seul.</p>
          </>
        )}
        {live && (
          <PresenceCounts
            registered={live.registered}
            connected={live.connected}
            cap={playersCapLabel(live)}
          />
        )}
        {live && !live.is_host && live.joined && (
          <p className="text-sm font-semibold text-[#a7f3d0]" data-testid="private-live-joined">
            ✓ Tu participes à ce défi
          </p>
        )}
      </ArenaPanel>

      {live?.is_host && (
        <HostControls live={live} code={code} userId={userId} awaitingHost={awaitingHost} />
      )}

      {live && live.players.length > 0 && <PlayersGrid players={live.players} />}

      {live && (
        <ArenaInfoList
          items={[
            { icon: ListChecks, text: privateLiveSummary(live.settings, live.step_count) },
            { icon: Trophy, text: 'Classement du défi en direct' },
            {
              icon: Sparkles,
              text: isGuest
                ? 'Crée ton compte pour gagner des XP et des HORA'
                : 'Des XP et l’HORA des SKILLCARDS à gagner',
            },
          ]}
        />
      )}

      {offerPractice && (
        <ScratchPracticeCard level={live?.level} autoOpen={!seenTutorial && !live?.is_host} />
      )}

      {live && !live.is_host && live.joined && live.status === 'scheduled' && (
        <LeaveButton live={live} code={code} />
      )}

      {live && !live.is_host && (
        <button
          type="button"
          className={liveCtaClass('glass')}
          onClick={() =>
            void shareContent({
              title: live.title ?? 'Défi live JeRaisonne',
              text: 'Rejoins ce défi live sur JeRaisonne !',
              url: privateLiveUrl(code),
            })
          }
        >
          <Share2 className="h-5 w-5" aria-hidden />
          Partager le défi
        </button>
      )}
    </div>
  );
}

/** Un joueur quitte le défi avant le départ (migration 0733) ; l'organisateur, lui, l'annule. */
function LeaveButton({ live, code }: { live: PrivateLive; code: string }) {
  const leave = useLeavePrivateLive(code);
  return (
    <div className="space-y-2">
      <button
        type="button"
        className={liveCtaClass('glass')}
        disabled={leave.isPending}
        onClick={() => {
          if (
            !window.confirm(
              'Te retirer de ce défi ? Tu pourras revenir avec le lien tant qu’il n’a pas commencé.',
            )
          )
            return;
          leave.mutate(live.id, {
            // la salle réinscrit en la rouvrant : on la quitte
            onSuccess: () => {
              window.location.href = '/';
            },
          });
        }}
      >
        <LogOut className="h-5 w-5" aria-hidden />
        {leave.isPending ? 'Retrait…' : 'Me retirer du défi'}
      </button>
      {leave.error && (
        <p role="alert" className="text-center text-sm text-[#fca5a5]">
          {privateLiveErrorMessage(leave.error.message)}
        </p>
      )}
    </div>
  );
}

export function PresenceCounts({
  registered,
  connected,
  cap = null,
}: {
  registered: number;
  connected: number;
  /** « 2/3 joueurs » quand le défi a un plafond (0728). */
  cap?: string | null;
}) {
  return (
    <div
      className="flex flex-wrap items-center justify-center gap-2"
      data-testid="private-live-counts"
    >
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(5,102,242,0.22)] px-3 py-1.5 text-sm font-semibold text-[#cfe2ff]">
        <Users className="h-4 w-4" aria-hidden />
        {registered} inscrit{registered > 1 ? 's' : ''}
      </span>
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(52,211,153,0.16)] px-3 py-1.5 text-sm font-semibold text-[#a7f3d0]">
        <Wifi className="h-4 w-4" aria-hidden />
        {connected} connecté{connected > 1 ? 's' : ''}
      </span>
      {cap && (
        <span
          className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(244,198,78,0.18)] px-3 py-1.5 text-sm font-semibold text-[#fde68a]"
          data-testid="private-live-cap"
        >
          {cap}
        </span>
      )}
    </div>
  );
}

/** Les joueurs en grands avatars : rôle ou présence sous chaque nom. */
function PlayersGrid({ players }: { players: LobbyPlayer[] }) {
  const ordered = lobbyPlayersOrder(players);
  const shown = ordered.slice(0, LOBBY_MAX_AVATARS);
  const more = ordered.length - shown.length;
  return (
    <ArenaPanel>
      <p className="text-ink-500 mb-3 text-xs font-semibold uppercase tracking-widest">Joueurs</p>
      <ul className="grid grid-cols-4 gap-x-2 gap-y-4">
        {shown.map((p, i) => (
          <li key={`${p.name}-${i}`} className="flex min-w-0 flex-col items-center text-center">
            <span className="relative">
              <AvatarCircle
                name={p.name}
                src={p.avatar_url}
                size="lg"
                className={cn(
                  'ring-[3px]',
                  p.is_host
                    ? 'ring-[#f4c64e]'
                    : p.connected
                      ? 'ring-[#34d399]'
                      : 'opacity-60 ring-[rgba(255,255,255,0.2)]',
                )}
              />
              {p.connected && !p.is_host && (
                <span
                  aria-hidden
                  className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#34d399] text-[#061433] ring-2 ring-[#061433]"
                >
                  <Check className="h-3 w-3" strokeWidth={3.5} />
                </span>
              )}
            </span>
            <span className="mt-1.5 w-full truncate text-sm font-semibold text-white">
              {p.name}
              {p.is_me && <span className="text-ink-500 font-normal"> (toi)</span>}
            </span>
            {p.is_host ? (
              <span className="mt-0.5 rounded-full bg-[#f4c64e] px-2 py-0.5 text-xs font-bold text-[#0a2e57]">
                Organisateur
              </span>
            ) : p.is_guest ? (
              <span className="text-ink-500 mt-0.5 text-xs font-medium">Invité</span>
            ) : null}
            <span className="sr-only">{p.connected ? 'connecté' : 'pas connecté'}</span>
          </li>
        ))}
        {more > 0 && (
          <li className="flex flex-col items-center text-center">
            <span className="font-display flex h-14 w-14 items-center justify-center rounded-full bg-[rgba(255,255,255,0.08)] text-base font-bold text-white ring-1 ring-[rgba(255,255,255,0.15)]">
              +{more}
            </span>
            <span className="text-ink-500 mt-1.5 text-xs">autre{more > 1 ? 's' : ''}</span>
          </li>
        )}
      </ul>
    </ArenaPanel>
  );
}

/** Remonte en haut de la page (et de tout conteneur qui défile) : le compte à rebours y est. */
export function scrollLobbyToTop(from: HTMLElement | null): void {
  let el = from?.parentElement ?? null;
  while (el) {
    if (el.scrollTop > 0) el.scrollTo({ top: 0, behavior: 'smooth' });
    el = el.parentElement;
  }
  if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
}

function HostControls({
  live,
  code,
  userId,
  awaitingHost,
}: {
  live: PrivateLive;
  code: string;
  /** Compte de l'hôte : dossier d'envoi d'une image de cover. */
  userId: string | null;
  awaitingHost: boolean;
}) {
  const start = useStartPrivateLive(code);
  const setCover = useSetPrivateLiveCover(code);
  const [scheduling, setScheduling] = useState(false);
  const [coverOpen, setCoverOpen] = useState(false);
  const [at, setAt] = useState(() => toLocalInput(Date.now() + 15 * 60_000));
  const [error, setError] = useState<string | null>(null);
  // « Démarrer maintenant » touché : le bouton disparaît tout de suite, la page remonte au compte à rebours
  const [startedNow, setStartedNow] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const url = privateLiveUrl(code);

  function run(action: 'start' | 'schedule' | 'cancel', isoAt?: string) {
    setError(null);
    if (action === 'start') {
      setStartedNow(true);
      scrollLobbyToTop(panelRef.current);
    }
    start.mutate(
      { challengeId: live.id, action, at: isoAt },
      {
        onSuccess: (r) => {
          setScheduling(false);
          if (r.cancelled) window.location.href = '/';
          if (action === 'start') scrollLobbyToTop(panelRef.current);
        },
        onError: (e) => {
          if (action === 'start') setStartedNow(false);
          setError(privateLiveErrorMessage(e.message));
        },
      },
    );
  }

  function confirmSchedule() {
    const ms = new Date(at).getTime();
    const issue = scheduleError(ms, Date.now());
    if (issue) {
      setError(issue);
      return;
    }
    run('schedule', new Date(ms).toISOString());
  }

  return (
    <ArenaPanel className="space-y-4 sm:p-5">
      <div ref={panelRef} className="flex flex-col items-center gap-2 text-center">
        <p className="text-ink-500 text-xs font-semibold uppercase tracking-widest">Code du défi</p>
        <p className="font-display text-3xl font-black tracking-widest text-[#f4c64e]">
          {formatJoinCode(code)}
        </p>
        <QrCodeImage
          value={url}
          size={176}
          alt="QR code du défi"
          className="rounded-2xl bg-[#ffffff] p-2"
        />
      </div>
      <InviteLinkField url={url} shareTitle={live.title ?? 'Défi live JeRaisonne'} />

      <div className="flex flex-col gap-2.5">
        {!startedNow && (
          <button
            type="button"
            className={liveCtaClass('gold')}
            disabled={start.isPending}
            onClick={() => run('start')}
          >
            <Play className="h-5 w-5" aria-hidden />
            Démarrer maintenant
          </button>
        )}
        {startedNow ? null : scheduling ? (
          <div className="space-y-2 rounded-2xl bg-[rgba(255,255,255,0.06)] p-3">
            <label className="text-ink-700 block text-sm font-semibold" htmlFor="private-live-at">
              Heure de départ
            </label>
            <input
              id="private-live-at"
              type="datetime-local"
              value={at}
              onChange={(e) => setAt(e.target.value)}
              className="h-11 w-full rounded-xl border border-[rgba(255,255,255,0.2)] bg-[rgba(6,20,51,0.6)] px-3 text-sm text-white [color-scheme:dark]"
            />
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <button
                type="button"
                className={liveCtaClass('glass', 'min-h-11 text-sm')}
                onClick={() => setScheduling(false)}
              >
                Retour
              </button>
              <button
                type="button"
                className={liveCtaClass('primary', 'min-h-11 text-sm')}
                disabled={start.isPending}
                onClick={confirmSchedule}
              >
                Programmer
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={liveCtaClass('glass', 'min-h-11 text-sm')}
            onClick={() => setScheduling(true)}
          >
            <CalendarClock className="h-4 w-4" aria-hidden />
            {awaitingHost ? 'Programmer une heure' : 'Changer l’heure'}
          </button>
        )}

        {coverOpen ? (
          <div className="space-y-3 rounded-2xl bg-[rgba(255,255,255,0.06)] p-3">
            <LiveCoverPicker
              label="Cover du défi"
              value={live.cover_path ?? null}
              userId={userId}
              onChange={(path) => {
                setError(null);
                setCover.mutate(
                  { challengeId: live.id, path },
                  { onError: () => setError('La cover n’a pas pu être changée. Réessaie.') },
                );
              }}
            />
            <button
              type="button"
              className={liveCtaClass('glass', 'min-h-11 text-sm')}
              onClick={() => setCoverOpen(false)}
            >
              {setCover.isPending ? 'Enregistrement…' : 'Terminé'}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={liveCtaClass('glass', 'min-h-11 text-sm')}
            onClick={() => setCoverOpen(true)}
          >
            <ImageIcon className="h-4 w-4" aria-hidden />
            Changer la cover
          </button>
        )}

        <button
          type="button"
          className="text-ink-600 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full text-sm font-semibold transition active:scale-[0.98]"
          onClick={() => {
            if (
              window.confirm('Annuler ce défi ? Les joueurs inscrits ne pourront plus le jouer.')
            ) {
              run('cancel');
            }
          }}
        >
          <X className="h-4 w-4" aria-hidden />
          Annuler le défi
        </button>
      </div>
      {error && (
        <p role="alert" className="text-center text-sm text-[#fca5a5]">
          {error}
        </p>
      )}
    </ArenaPanel>
  );
}
